import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface RatingDisplayProps {
  rating: number;
  reviewCount: number;
  type: "hotel" | "restaurant";
  className?: string;
  theme?: "light" | "dark"; // light for dark backgrounds (white text), dark for light backgrounds (gray text)
}

const RatingDisplay: React.FC<RatingDisplayProps> = ({
  rating,
  reviewCount,
  type,
  className = "",
  theme = "light", // default to white text for CTA sections with dark backgrounds
}) => {
  const { t } = useLanguage();

  // Generate array of stars (filled and empty) - using Math.round for better visual representation
  const stars = Array.from({ length: 5 }, (_, index) => {
    const starPosition = index + 1;
    const isFilled = starPosition <= Math.round(rating);
    const isHalf = starPosition === Math.ceil(rating) && rating % 1 !== 0;

    return { isFilled, isHalf };
  });

  // Get Google Business Profile direct link based on type
  const googleLink =
    type === "hotel"
      ? "https://www.google.com/search?q=Beatenberg+Resort+%26+Spa&stick=H4sIAAAAAAAA_-NgU1I1qDAxt0hLNDUxTDQ1s0w0NkqxMqgwtzAzNrVMM01KSQMKp5osYhV3Sk0sSc1LSi1KVwhKLc4vKlFQUwguSAQAqmrqYEMAAAA&hl=en&mat=CUd0QWESeEYXElcBTVDHnmbBPE0CyrRIEQIuBOYDSQYehfn5ngWCYQrL1sAw3eXuK3CLPGmcBarvAYkD7calEPyLYkQ9-bsOsbeaueOlYKzVeVI-BIQdcN6Ed9WqiuCi5NM&authuser=0"
      : "https://www.google.com/search?q=Restaurant+Jungfrau&stick=H4sIAAAAAAAA_-NgU1I1qDAxt0hLNE1KMU0zMDE0MTK0MqhIMbCwtEhOtkhNs0g2Mkg0X8QqHJRaXJJYWpSYV6LgVZqXnlaUWAoAA7dRoz8AAAA&hl=en&mat=Cf_WI2ox2Uu-ElYBTVDHngpgbTiBjR7iGDKXs2YSZS-ZUmeDsirtgj1iEdieXJ_sNG9L_15rdxOrP32IOyqa8Y3-DmeSNynlxRz8mYRG14T6-0QknCpTCa1qJhak3-MMTA&authuser=0";

  // Determine text colors based on theme
  const textColors =
    theme === "light"
      ? {
          rating: "text-elegant-white/90",
          separator: "text-elegant-white/50",
          link: "text-elegant-white/80 hover:text-elegant-white",
        }
      : {
          rating: "text-gray-700",
          separator: "text-gray-400",
          link: "text-gray-600 hover:text-gray-800",
        };

  return (
    <div
      className={`inline-flex items-center gap-2 text-sm ${className}`}
      data-oid="njgad5s"
    >
      {/* Star Rating */}
      <div className="flex gap-0.5" data-oid="k309hc9">
        {stars.map((star, index) => (
          <Star
            key={index}
            className={`w-4 h-4 ${
              star.isFilled
                ? "text-yellow-400 fill-yellow-400"
                : "text-gray-300 fill-gray-300"
            }`}
            data-oid="51ds9zp"
          />
        ))}
      </div>

      {/* Rating Number */}
      <span className={`font-medium ${textColors.rating}`} data-oid="3d4.pn4">
        {rating.toFixed(1)}
      </span>

      {/* Separator */}
      <span className={textColors.separator} data-oid="owj_h6t">
        ·
      </span>

      {/* Review Count Link */}
      <a
        href={googleLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`${textColors.link} transition-colors underline-offset-2 hover:underline`}
        data-oid="7rg.kio"
      >
        {reviewCount} {t(`ratings.${type}.reviews`)}
      </a>
    </div>
  );
};

export default RatingDisplay;
